import { IFRAME_WIDGETS } from '../canvas-editor-v2/config';
import { getDateAndTimeSource } from '../canvas-editor-v2/lib/FabricJs/utils/handleWorldClockAndDate';
import {
  getCurrentPlayingVideoDetailsHeaderTxtElement,
  getCurrentPlayingVideoDetailsTxtElement,
  getVideoPlayingTxtElement,
  getVideoScreenshotRestrictedTxtElement,
  getWrapperElement,
} from './screenshotLayer';

import { ShapeType } from '../canvas-editor-v2/types';
import {
  getObjectOpacity,
  getObjectTransform,
  getTextSpacing,
} from './objectStyle';

const { WORLD_CLOCK, DATE, IFRAME } = IFRAME_WIDGETS;

const handleRatio = (element: any, object: any, displayRatio: number) => {
  element.style.position = 'absolute';
  element.style.display = 'block';
  element.width = object.width;
  element.height = object.height;
  element.style.left = `${object.left * displayRatio}px`;
  element.style.top = `${object.top * displayRatio}px`;
  const width = object.width * object.scaleX * displayRatio;
  const height = object.height * object.scaleY * displayRatio;
  element.style.width = `${width}px`;
  element.style.height = `${height}px`;
  element.style.transformOrigin = `${object.originX} ${object.originY}`;
  element.style.transform = getObjectTransform(object, width, height);
  element.style.opacity = getObjectOpacity(object);
};

const getAndSetAnimations = (dataObj: any) => {
  if (
    !dataObj ||
    !dataObj?.animation ||
    !dataObj?.animationKeyframes ||
    !dataObj?.animationId
  ) {
    return null;
  }
  let animationStr = dataObj?.animation.split('animation: ')[1];
  if (animationStr.endsWith(';')) {
    animationStr = animationStr.slice(0, -1);
  }
  const uniqueId = dataObj?.animationId;

  const existingStyleSheet = document.getElementById(uniqueId);
  if (existingStyleSheet) {
    existingStyleSheet.remove();
  }

  const styleSheet = document.createElement('style');
  // Create a unique identifier
  styleSheet.id = uniqueId;
  styleSheet.innerHTML = dataObj?.animationKeyframes;
  document.head.appendChild(styleSheet);

  return animationStr;
};

const onAddImage = (
  obj: any,
  mainDiv: Element,
  displayRatio: number,
  assetsList: any,
) => {
  // Scoped to mainDiv (not document.getElementById) - object ids are stable
  // across republishes of unchanged content, and a document-wide lookup can
  // find a stale element still attached to a different, previous
  // PresentationSlide instance's div (kept around briefly during the
  // crossfade) and move it out from under that instance, leaving the new
  // slide's div empty once the old instance is cleaned up.
  const existsDomElement = mainDiv.querySelector(
    `#${CSS.escape(obj.id)}`,
  ) as HTMLImageElement;
  if (existsDomElement) {
    existsDomElement.id = obj.id;
    handleRatio(existsDomElement, obj, displayRatio);
    mainDiv.appendChild(existsDomElement);
  } else if (obj.data.assetId && assetsList[obj.data.assetId]) {
    const cachedImage = assetsList[obj.data.assetId] as HTMLImageElement;
    const img = cachedImage.cloneNode(true) as HTMLImageElement;
    img.setAttribute('id', obj.id);
    const animation = getAndSetAnimations(obj?.data);
    if (animation) {
      img.style.animation = animation;
    }
    handleRatio(img, obj, displayRatio);
    mainDiv.appendChild(img);
  }
};

const handleRatioVideo = (element: any, object: any, displayRatio: number) => {
  element.style.position = 'absolute';
  element.style.display = 'block';
  element.width = object.width;
  element.height = object.height;
  element.style.top = '0px';
  element.style.width = `${object.width * object.scaleX * displayRatio}px`;
  element.style.height = `${object.height * object.scaleY * displayRatio}px`;
  element.style.transformOrigin = `${object.originX} ${object.originY}`;
};

const setVideoDetails: any = (
  element: HTMLVideoElement,
  object: any,
  displayRatio: number,
  assetId: string,
  deviceBrand?: string,
) => {
  const { innerWidth } = window;
  const videoRatio =
    (object.width * object.scaleX * displayRatio) / (innerWidth * 2);
  const wrapperDiv = getWrapperElement(
    object,
    displayRatio,
    assetId,
    deviceBrand,
  );
  const videoPlayingTxt = getVideoPlayingTxtElement(videoRatio);
  const videoScreenshotRestrictedTxt =
    getVideoScreenshotRestrictedTxtElement(videoRatio);
  const currentPlayingVideoNameHeaderTxt =
    getCurrentPlayingVideoDetailsHeaderTxtElement(
      'vid-detail-name-header-p',
      videoRatio,
    );
  currentPlayingVideoNameHeaderTxt.innerHTML = 'This is the current Video';
  const currentPlayingVideoNameTxt = getCurrentPlayingVideoDetailsTxtElement(
    'vid-detail-name-p',
    videoRatio,
  );
  const currentPlayingVideoTimeHeaderTxt =
    getCurrentPlayingVideoDetailsHeaderTxtElement(
      'vid-detail-current-time-header-p',
      videoRatio,
    );
  currentPlayingVideoTimeHeaderTxt.innerHTML =
    'This is the currently playing time frame';
  const currentPlayingVideoTimeTxt = getCurrentPlayingVideoDetailsTxtElement(
    'vid-detail-current-time-p',
    videoRatio,
  );
  wrapperDiv.appendChild(videoPlayingTxt);
  wrapperDiv.appendChild(videoScreenshotRestrictedTxt);
  wrapperDiv.appendChild(currentPlayingVideoNameHeaderTxt);
  wrapperDiv.appendChild(currentPlayingVideoNameTxt);
  wrapperDiv.appendChild(currentPlayingVideoTimeHeaderTxt);
  wrapperDiv.appendChild(currentPlayingVideoTimeTxt);
  wrapperDiv.appendChild(element);
  return wrapperDiv;
};

const onAddVideo = async (
  object: any,
  mainDiv: Element,
  displayRatio: number,
  assetsList: any,
  deviceBrand?: string,
  stage?: string,
) => {
  const isOneAsset = Object.keys(assetsList).length === 1;
  let shouldRestart = false;
  // Scoped to mainDiv - see the comment in onAddImage for why a
  // document-wide lookup is unsafe here.
  const existsDomElement = mainDiv.querySelector(
    `#${CSS.escape(object.id)}`,
  ) as HTMLVideoElement;
  if (existsDomElement) {
    handleRatioVideo(existsDomElement, object, displayRatio);
    existsDomElement.id = object.id;
    existsDomElement.autoplay = true;
    existsDomElement.controls = false;
    existsDomElement.loop = isOneAsset;
    existsDomElement.preload = 'auto';
    existsDomElement.muted = stage === 'leave';
    mainDiv.appendChild(existsDomElement);
  } else if (object.data.assetId) {
    const videoEl = assetsList[object.data.assetId] as HTMLVideoElement;
    if (!videoEl) {
      return;
    }
    videoEl.id = object.id;
    videoEl.autoplay = true;
    videoEl.controls = false;
    videoEl.preload = 'auto';
    videoEl.poster = object?.data?.thumbnailUrl || '';
    videoEl.muted = stage === 'leave';
    videoEl.width = object.width;
    videoEl.height = object.height;
    videoEl.loop = isOneAsset;
    videoEl.style.zIndex = '1';
    shouldRestart = true;
    handleRatioVideo(videoEl, object, displayRatio);
    const wrapperDiv = setVideoDetails(
      videoEl,
      object,
      displayRatio,
      object.data.assetId,
      deviceBrand,
    );
    mainDiv.appendChild(wrapperDiv);
  }

  const videoEl = assetsList[object.data.assetId] as HTMLVideoElement;
  if (videoEl && videoEl.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    await new Promise<void>((resolve) => {
      const ready = () => {
        window.clearTimeout(timeoutId);
        videoEl.removeEventListener('loadeddata', ready);
        videoEl.removeEventListener('error', ready);
        resolve();
      };
      const timeoutId = window.setTimeout(ready, 5000);
      videoEl.addEventListener('loadeddata', ready, { once: true });
      videoEl.addEventListener('error', ready, { once: true });
    });
  }

  if (videoEl && stage !== 'leave') {
    if (shouldRestart || videoEl.ended) {
      videoEl.currentTime = 0;
    }
    try {
      await videoEl.play();
    } catch {
      // Preserve audio where autoplay is allowed, but keep signage moving on
      // browsers/WebViews that only permit muted autoplay.
      videoEl.muted = true;
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => {
          videoEl.play().then(resolve).catch(resolve);
        });
      });
    }
  }
};

const onAddIframe = (object: any, mainDiv: Element, displayRatio: number) => {
  // Scoped to mainDiv - see the comment in onAddImage for why a
  // document-wide lookup is unsafe here.
  const existingIframe = mainDiv.querySelector(
    `#${CSS.escape(object.id)}`,
  ) as HTMLIFrameElement;
  const iframe = existingIframe || document.createElement('iframe');
  mainDiv.appendChild(iframe);
  iframe.id = object.id;
  iframe.className = object?.data?.className;
  iframe.allow = 'autoplay; encrypted-media';
  iframe.src = object?.data?.src;
  handleRatio(iframe, object, displayRatio);
};

const onAddClock = (object: any, mainDiv: Element, displayRatio: number) => {
  // Scoped to mainDiv - see the comment in onAddImage for why a
  // document-wide lookup is unsafe here.
  const existingIframe = mainDiv.querySelector(
    `#${CSS.escape(object.id)}`,
  ) as HTMLIFrameElement;
  const iframe = existingIframe || document.createElement('iframe');
  mainDiv.appendChild(iframe);
  iframe.id = object.id;
  iframe.className = object?.data?.className;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const width = object.width * object.scaleX * displayRatio;
  const height = object.height * object.scaleY * displayRatio;
  iframe.style.border = 'none';
  iframe.style.backgroundColor = object.fill;
  iframe.srcdoc = getDateAndTimeSource(
    timezone,
    width,
    height,
    object.data.color,
  );

  handleRatio(iframe, object, displayRatio);
};

const onAddText = (
  object: any,
  mainDiv: Element,
  displayRatio: number,
  kvsData: any,
) => {
  // Scoped to mainDiv - see the comment in onAddImage for why a
  // document-wide lookup is unsafe here.
  const existsTextElement = mainDiv.querySelector(
    `#${CSS.escape(object.id)}`,
  ) as HTMLElement;
  let textElement;
  const animation = getAndSetAnimations(object?.data);

  if (existsTextElement) {
    existsTextElement.style.display = 'block';
    existsTextElement.style.width = `${object.width}px`;
    existsTextElement.style.height = `${object.height}px`;
    existsTextElement.style.whiteSpace = 'pre';
    textElement = existsTextElement;
  } else {
    textElement = document.createElement('span');
  }
  textElement.innerHTML = object.text;
  textElement.id = object.id;
  const fontSize = object.fontSize * object.scaleX * displayRatio;
  const spacing = getTextSpacing(object, fontSize);
  textElement.style.fontSize = `${fontSize}px`;

  textElement.style.position = 'absolute';
  textElement.style.display = 'block';
  textElement.style.left = `${object.left * displayRatio}px`;
  textElement.style.top = `${object.top * displayRatio}px`;
  textElement.style.color = object.fill;
  textElement.style.fontFamily = object.fontFamily;
  textElement.style.fontWeight = object.fontWeight;
  textElement.style.fontStyle = object.fontStyle;
  textElement.style.textAlign = object.textAlign;
  textElement.style.lineHeight = spacing.lineHeight;
  textElement.style.letterSpacing = spacing.letterSpacing;
  textElement.style.textDecoration = object.underline ? 'underline' : '';
  textElement.style.margin = '0px';
  textElement.style.transformOrigin = `${object.originX} ${object.originY}`;
  textElement.style.transform =
    getObjectTransform(
      object,
      object.width * object.scaleX * displayRatio,
      object.height * object.scaleY * displayRatio,
    ) + (spacing.offsetY ? ` translateY(${-spacing.offsetY}px)` : '');
  textElement.style.opacity = getObjectOpacity(object);
  textElement.style.whiteSpace = 'pre';

  if (animation) {
    textElement.style.animation = `${animation}`;
  }

  mainDiv.appendChild(textElement);
  if (object?.data?.type === 'kvs') {
    const kvsId = object?.data?.kvsId;
    if (kvsId && kvsData[kvsId]) {
      textElement.innerHTML = kvsData[kvsId];
    }
  }
};

const onAddCircle = (object: any, mainDiv: Element, displayRatio: number) => {
  // Scoped to mainDiv - see the comment in onAddImage for why a
  // document-wide lookup is unsafe here.
  const existsDivElement = mainDiv.querySelector(
    `#${CSS.escape(object.id)}`,
  ) as HTMLElement;
  let divElement;
  if (existsDivElement) {
    existsDivElement.style.display = 'block';
    divElement = existsDivElement;
  } else {
    divElement = document.createElement('div');
  }
  divElement.id = object.id;
  divElement.style.borderRadius = '50%';
  divElement.style.backgroundColor = object.fill;

  const animation = getAndSetAnimations(object?.data);
  if (animation) {
    divElement.style.animation = animation;
  }

  handleRatio(divElement, object, displayRatio);
  mainDiv.appendChild(divElement);
};

const onAddRectangle = (
  object: any,
  mainDiv: Element,
  displayRatio: number,
) => {
  // Scoped to mainDiv - see the comment in onAddImage for why a
  // document-wide lookup is unsafe here.
  const existsDivElement = mainDiv.querySelector(
    `#${CSS.escape(object.id)}`,
  ) as HTMLElement;
  let divElement;
  if (existsDivElement) {
    existsDivElement.style.display = 'block';
    divElement = existsDivElement;
  } else {
    divElement = document.createElement('div');
  }
  divElement.id = object.id;
  divElement.style.backgroundColor = object.fill;

  const animation = getAndSetAnimations(object?.data);
  if (animation) {
    divElement.style.animation = animation;
  }

  handleRatio(divElement, object, displayRatio);
  mainDiv.appendChild(divElement);
};

// Shapes added via the Shapes panel (anything beyond a plain circle/rect)
// are fabric.Path objects. Fabric's serialized JSON stores `path` as a
// parsed command array rather than the original "d" string, so the exact
// path data + fill rule is kept on `data.svgPath`/`data.fillRule` at
// creation time (see ShapesWidget.tsx) and rebuilt into an inline SVG data
// URI here, rendered as an <img> (matching onAddImage's element type, which
// is what handleRatio's element.width/height assignments require).
const onAddShape = (object: any, mainDiv: Element, displayRatio: number) => {
  // Scoped to mainDiv - see the comment in onAddImage for why a
  // document-wide lookup is unsafe here.
  const existsImgElement = mainDiv.querySelector(
    `#${CSS.escape(object.id)}`,
  ) as HTMLImageElement;
  const imgElement = existsImgElement || document.createElement('img');
  imgElement.id = object.id;

  const svgPath = object?.data?.svgPath;
  const fillRule = object?.data?.fillRule || 'nonzero';
  const fill = object.fill || 'black';
  // QR codes need a light margin to scan; their path spans the whole
  // 100x100 view box, so the background matches the editor.
  const background = object.backgroundColor
    ? `<rect width="100" height="100" fill="${object.backgroundColor}"/>`
    : '';
  if (svgPath) {
    const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">${background}<path d="${svgPath}" fill="${fill}" fill-rule="${fillRule}"/></svg>`;
    imgElement.src = `data:image/svg+xml;utf8,${encodeURIComponent(svgMarkup)}`;
  }

  const animation = getAndSetAnimations(object?.data);
  if (animation) {
    imgElement.style.animation = animation;
  }

  handleRatio(imgElement, object, displayRatio);
  mainDiv.appendChild(imgElement);
};

const onAddDate = (object: any, mainDiv: Element, displayRatio: number) => {
  // Scoped to mainDiv - see the comment in onAddImage for why a
  // document-wide lookup is unsafe here.
  const existingIframe = mainDiv.querySelector(
    `#${CSS.escape(object.id)}`,
  ) as HTMLIFrameElement;
  const iframe = existingIframe || document.createElement('iframe');
  mainDiv.appendChild(iframe);
  iframe.id = object.id;
  iframe.className = object?.data?.className;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const width = object.width * object.scaleX * displayRatio;
  const height = object.height * object.scaleY * displayRatio;
  iframe.style.border = 'none';
  iframe.style.backgroundColor = object.fill;
  iframe.srcdoc = getDateAndTimeSource(
    timezone,
    width,
    height,
    object.data.color,
    false,
  );

  handleRatio(iframe, object, displayRatio);
};

export const initCanvas = async (
  slide: any,
  displayRatio: number,
  kvsData: any,
  assetsList: any,
  mainDiv: Element | null,
  deviceBrand?: string,
  stage?: string,
) => {
  // mainDiv is the calling PresentationSlide instance's own div, passed in
  // via ref rather than looked up here by class name. The old (leaving) and
  // new (entering) instances share the same "<id>_minimap" class whenever a
  // republish swaps in data for the slide currently on screen, so a
  // document.getElementsByClassName lookup here would be ambiguous between
  // them during the crossfade and could draw into whichever one happens to
  // be removed next, leaving the surviving instance blank.
  if (!mainDiv) {
    return;
  }
  const objects = slide?.slideData?.projectData?.objects || [];

  // getting the iframe widgets to the end of object array so that the widgets stay on top.
  for (const [i, object] of objects.entries()) {
    if (Object.values(IFRAME_WIDGETS).includes(object?.data?.type))
      objects.push(...objects.splice(i, 1));
  }

  for (const object of objects) {
    switch (object.type) {
      case 'text':
      case 'i-text':
        onAddText(object, mainDiv, displayRatio, kvsData);
        break;
      case 'image':
        if (object?.data?.type === 'video') {
          await onAddVideo(
            object,
            mainDiv,
            displayRatio,
            assetsList,
            deviceBrand,
            stage,
          );
        } else onAddImage(object, mainDiv, displayRatio, assetsList);
        break;
      case ShapeType.Circle:
      case 'ellipse':
        // border-radius: 50% on a non-square box still renders a correct
        // ellipse, so the same renderer covers both.
        onAddCircle(object, mainDiv, displayRatio);
        break;
      case 'path':
        onAddShape(object, mainDiv, displayRatio);
        break;
      case ShapeType.Rectangle:
        const { type } = object?.data || {};
        if (type === IFRAME) {
          onAddIframe(object, mainDiv, displayRatio);
        } else if (type === WORLD_CLOCK) {
          onAddClock(object, mainDiv, displayRatio);
        } else if (type === DATE) {
          onAddDate(object, mainDiv, displayRatio);
        } else {
          onAddRectangle(object, mainDiv, displayRatio);
        }
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Not implement yet....');
    }

    object.selectable = false;
    if (object?.data?.type === 'video') {
      object.src = '';
    }
  }
};
